import { apiFetch } from '../../../../utils/utils';
import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Table,
  TableContainer,
  Tag,
  TagLabel,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Switch,
  HStack,
  Text,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { getBackendURL, getBackendURLWithSuffix, setAndClear } from '../../../../utils/utils';

function DigiCardTemplatesPage() {
  const [templates, setTemplates] = useState([]);

  const [templateName, setTemplateName] = useState('');

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit] = useState(10);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [inProgress, setInProgress] = useState(false);

  useEffect(() => {
    fetchTemplates(page);
  }, [page]);

  const fetchTemplates = (pageNum) => {
    apiFetch(`${getBackendURL()}/digicard/templates?page=${pageNum}&limit=${limit}`)
      .then(response => response.json())
      .then(response => {
        if (response !== null && response.templates && response.templates.length > 0) {
          setTemplates(response.templates);
          setTotalPages(response.totalPages);
        } else {
          setTemplates([]);
          setTotalPages(1);
        }
      })
      .catch(error => {
        console.log(error);
        setTemplates([]);
      });
  };

  const onToggleStatus = (id, currentStatus) => {
    apiFetch(`${getBackendURL()}/digicard/templates/toggle`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: id,
        status: !currentStatus,
      }),
    })
      .then(response => response.json())
      .then(response => {
        if (response.updated) {
          fetchTemplates(page);
        }
      })
      .catch(error => {
        console.log(error);
      });
  };

  const onSubmit = () => {
    setError('');
    if (templateName === '') {
      setError('Please set the proper details');
      return;
    }
    setInProgress(true);
    apiFetch(`${getBackendURL()}/digicard/templates/add`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: templateName,
      }),
    })
      .then(response => response.json())
      .then(response => {
        setTemplateName('');
        setInProgress(false);
        if (response.template) {
          setAndClear(response.message, setSuccess);
          fetchTemplates(page);
        } else {
          setAndClear(response.message, setError);
        }
      })
      .catch(error => {
        setInProgress(false);
        setAndClear(error, setError);
      });
  };

  return (
    <>
      <Breadcrumb marginTop={2} marginLeft={2}>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/">
            Home
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={Link} to="/digicard/templates">
            Templates
          </BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>
      <Stack mt={4} direction={{ base: 'column', lg: 'row' }}>
        <Flex flex={0.7}>
          <Box w={'100%'}>
            <Card bg={useColorModeValue('white', 'gray.700')}>
              <CardHeader>
                <Flex spacing="4">
                  <Heading size="md">Templates</Heading>
                </Flex>
              </CardHeader>
              <Divider />
              <CardBody>
                <TableContainer data-testid="plans-table">
                  <Table variant="simple">
                    <Thead>
                      <Tr>
                        <Th>Sr. No</Th>
                        <Th>Name</Th>
                        <Th>Template Id</Th>
                        <Th>Status</Th>
                        <Th>Action</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {templates.map((template, index) => {
                        return (
                          <Tr key={template.templateId}>
                            <Td>{index + 1 + (page - 1) * limit}</Td>
                            <Td>{template.name}</Td>
                            <Td>{template.templateId}</Td>
                            <Td>
                              <Tag
                                size="md"
                                colorScheme={template.status ? 'green' : 'red'}
                                borderRadius="full"
                              >
                                <TagLabel>
                                  {template.status
                                    ? 'Activated'
                                    : 'Deactivated'}
                                </TagLabel>
                              </Tag>
                            </Td>
                            <Td>
                              <Switch
                                isChecked={template.status}
                                onChange={() =>
                                  onToggleStatus(template._id, template.status)
                                }
                              />
                            </Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </TableContainer>
              </CardBody>
              <Divider />
              <CardFooter justifyContent="center">
                <HStack spacing={4}>
                  <Button
                    onClick={() => setPage(page - 1)}
                    isDisabled={page === 1}
                  >
                    Previous
                  </Button>
                  <Text>
                    Page {page} of {totalPages}
                  </Text>
                  <Button
                    onClick={() => setPage(page + 1)}
                    isDisabled={page === totalPages}
                  >
                    Next
                  </Button>
                </HStack>
              </CardFooter>
            </Card>
          </Box>
        </Flex>
        <Flex flex={0.3}>
          <Box w={'100%'}>
            <Card bg={useColorModeValue('white', 'gray.700')}>
              <CardHeader>
                <Heading size="md">{'Add Template'}</Heading>
              </CardHeader>
              <Divider />
              <CardBody>
                <Alert status="success" display={success ? 'flex' : 'none'}>
                  <AlertIcon />
                  {success}
                </Alert>
                <Alert status="error" display={error ? 'flex' : 'none'}>
                  <AlertIcon />
                  {error}
                </Alert>
                <Stack spacing="4" mt="2">
                  <FormControl>
                    <FormLabel>Template Name</FormLabel>
                    <Input
                      type="text"
                      id="template-name"
                      value={templateName}
                      name="template-name"
                      onChange={event => setTemplateName(event.target.value)}
                    />
                  </FormControl>
                </Stack>
              </CardBody>
              <Divider />
              <CardFooter>
                <ButtonGroup spacing="2">
                  <Button
                    variant="solid"
                    colorScheme="teal"
                    onClick={onSubmit}
                    isLoading={inProgress}
                    loadingText="Submitting..."
                  >
                    Add Template
                  </Button>
                </ButtonGroup>
              </CardFooter>
            </Card>
          </Box>
        </Flex>
      </Stack>
    </>
  );
}

export default DigiCardTemplatesPage;
